import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React from "react";
import { NotesOutlined, PublicOutlined, QrCode2Outlined, SettingsInputAntennaOutlined } from "@mui/icons-material";

import { useAuthContext } from "../../context/auth";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useDialogStyles from "../../hooks/useDialogStyles";
import { countryOptions } from "../../constants/countries";
import { AddButton, DialogHeader, FormSection, SubmitButton, SwitchField } from "../dialogKit";

const MODES = [
  { value: "M1", label: "Mode 1" },
  { value: "M2", label: "Mode 2" },
  { value: "M3A", label: "Mode 3/A" },
];

const Add = ({ onCreated }) => {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.secondary.main;

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedMode, setSelectedMode] = useState("");
  const [enabled, setEnabled] = useState(true);

  const [countryISO, setCountryISO] = useState("");
  const codeRef = useRef("");
  const descriptionRef = useRef("");

  const resetForm = () => {
    setCountryISO("");
    setSelectedMode("");
    setEnabled(true);

    if (codeRef.current) {
      codeRef.current.value = "";
    }

    if (descriptionRef.current) {
      descriptionRef.current.value = "";
    }

    setError(null);
  };

  const handleAdd = async () => {
    if (!auth) {
      setError("You must be logged in");
      return;
    }

    if (!selectedMode) {
      setError("Please select SIF Mode");
      return;
    }

    if (!countryISO) {
      setError("Please select a country");
      return;
    }

    const code = codeRef.current.value.trim();
    const description = descriptionRef.current.value.trim();

    if (!/^[A-Z]{2}$/.test(countryISO)) {
      setError("Country ISO must contain exactly 2 letters, for example TZ");
      return;
    }

    if (!code) {
      setError("SIF Code is required");
      return;
    }

    const newSIFCode = {
      mode: selectedMode,
      code,
      countryISO,
      description,
      enabled,
    };

    setSaving(true);
    try {
      const response = await axiosPrivate.post("/api/sifcodes", newSIFCode);

      if (onCreated) {
        onCreated(response.data);
      }

      setShow(false);
      resetForm();
    } catch (error) {
      console.error("Failed to create SIF code:", error);

      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to create SIF code"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!show || saving) return;

    setShow(false);
    resetForm();
  };

  const flag = (code) => (
    <span className={`fi fi-${code.toLowerCase()}`} style={{ flexShrink: 0, borderRadius: 2 }} />
  );

  return (
    <>
      <AddButton
        label="Add SIF Code"
        onClick={() => {
          setShow(true);
          setError(null);
        }}
      />

      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: ds.paperSx }}
      >
        <DialogHeader
          icon={SettingsInputAntennaOutlined}
          title="New SIF code"
          subtitle="Identification code reported by transponders"
          onClose={handleClose}
          disabled={saving}
        />

        <DialogContent sx={ds.contentSx}>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
            <FormSection icon={QrCode2Outlined} title="Code" />

            {/* Mode */}
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 0.5 }}>
                SIF Mode
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={selectedMode}
                onChange={(e, v) => { if (v) setSelectedMode(v); }}
                aria-label="SIF Mode"
                sx={{
                  height: 40,
                  p: 0.375,
                  gap: 0.5,
                  borderRadius: 1.5,
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.28)" : theme.palette.neutral[300]}`,
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : theme.palette.background.default,
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: "6px !important",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: theme.palette.text.secondary,
                    "&:hover": { backgroundColor: alpha(accent, 0.08) },
                    "&.Mui-selected, &.Mui-selected:hover": {
                      color: accent,
                      backgroundColor: alpha(accent, 0.14),
                      boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`,
                    },
                  },
                }}
              >
                {MODES.map((m) => (
                  <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* SIF Code */}
            <TextField
              size="small"
              label="SIF Code"
              placeholder="4521"
              inputRef={codeRef}
              fullWidth
              autoComplete="off"
              sx={ds.fieldSx}
            />

            {/* Status */}
            <SwitchField
              label="Enabled"
              checked={enabled}
              onChange={setEnabled}
              onText="Used to identify targets"
              offText="Kept, but not used"
            />

            <FormSection icon={PublicOutlined} title="Country" />

            {/* Country ISO */}
            <FormControl fullWidth size="small" sx={{ ...ds.fieldSx, minWidth: 0, gridColumn: "1 / -1" }}>
              <InputLabel>Country</InputLabel>

              <Select
                value={countryISO}
                onChange={(e) => setCountryISO(e.target.value)}
                label="Country"
                MenuProps={ds.menuProps}
                renderValue={(selected) => {
                  const country = countryOptions.find(
                    (item) => item.code === selected
                  );

                  if (!country) {
                    return "Select Country";
                  }

                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
                      {flag(country.code)}
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {country.name} ({country.code})
                      </span>
                    </Box>
                  );
                }}
              >
                {countryOptions.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                      {flag(country.code)}
                      <span>{country.name} ({country.code})</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormSection icon={NotesOutlined} title="Notes" hint="optional" />

            {/* Description */}
            <TextField
              fullWidth
              size="small"
              label="Description"
              placeholder="Enter description"
              inputRef={descriptionRef}
              multiline
              minRows={2}
              sx={{ ...ds.fieldSx, gridColumn: "1 / -1" }}
            />
          </Box>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={handleClose} disabled={saving} sx={ds.cancelButtonSx}>
            Cancel
          </Button>

          <SubmitButton
            label="Add SIF Code"
            busyLabel="Creating…"
            busy={saving}
            onClick={handleAdd}
            icon={<SettingsInputAntennaOutlined />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Add;
